import { AsGrid, AsSearchBar, mapPageInfo } from '@adserve/adserve-react-components';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { apiParamsReducer, defaultPageInfo } from '../../../services/AsGridService';
import { GetShowNetworks, GetShowsWithAvails } from '../../../services/DataService';
import { tabAreaStyle, tableAreaStyle } from '../../../styles';
import { gridContainerStyle, gridItemStyle, sectionedPanelStyle } from '../CampaignStyles';
import LogSelection from '../CampaignPackages/LogSelection';
import ShowsGridColumnDefinitions from './ShowsGridColumnDefinitions';
import { showTitleStyle } from './ShowStyles';

const ShowPanel = ({ campaign, onSelectShow }) => {
    const [shows, setShows] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [selectedStations, setSelectedStations] = useState([]);
    const [canLoad, setCanLoad] = useState(false);
    const [pageInfo, setPageInfo] = useState(defaultPageInfo);

    const { dateFormat, timeFormat, locale, currency } = useContext(SystemSettingsContext);

    const apiParamDefaults = {
        sortField: 'name',
        sortOrder: 'asc',
        pageNumber: 1,
        pageSize: 15,
        reservations: false,
        stationIds: []
    };

    const [apiParams, apiParamsDispatcher] = useReducer(apiParamsReducer, apiParamDefaults);

    const [columnDefinitions, setColumnDefinitions] = useState(ShowsGridColumnDefinitions());

    useEffect(() => {
        GetShowNetworks().then((data) => {
            data?.data.sort((a, b) =>
                a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0
            );

            // The screen starts with all stations selected, so populate selected stations.
            // At this stage it is a basic object which will get filled out by the log selection
            // component with what it needs
            const stations = [];
            for (let i = 0; i < data?.data.length; i++) {
                for (let j = 0; j < data?.data[i].subNetworks.length; j++) {
                    for (let k = 0; k < data?.data[i].subNetworks[j].subNetwork.subNetworkStations.length; k++) {
                        if (
                            stations.findIndex(
                                (x) => x.id === data?.data[i].subNetworks[j].subNetwork.subNetworkStations[k].stationId
                            ) === -1
                        ) {
                            stations.push({
                                id: data?.data[i].subNetworks[j].subNetwork.subNetworkStations[k].stationId,
                                name: data?.data[i].subNetworks[j].subNetwork.subNetworkStations[k].station.name
                            });
                        }
                    }
                }
            }

            setNetworks(data?.data);
            setSelectedStations(stations);
        });
    }, []);

    useEffect(() => {
        setCanLoad(true);
        apiParamsDispatcher({
            stationIds: selectedStations?.map((s) => s.id)
        });
    }, [selectedStations]);

    useEffect(() => {
        if (canLoad) {
            loadData();
        }
    }, [apiParams]);

    const loadData = () => {
        GetShowsWithAvails({
            ...apiParams,
            startDate: campaign?.startDate,
            endDate: campaign?.endDate
        }).then((data) => {
            setShows(data.data);
            setPageInfo(mapPageInfo(data));
        });
    };

    const handleSearchUpdate = (value) => {
        apiParamsDispatcher({ name: value });
    };

    const handlePageSizeChange = (option) => {
        apiParamsDispatcher({ pageSize: option.value, pageNumber: 1 });
    };

    const handleNextClick = () => {
        const newPageNumber = apiParams.pageNumber + 1;
        if (newPageNumber <= pageInfo.pageCount) {
            apiParamsDispatcher({ ...apiParams, pageNumber: newPageNumber });
        }
    };

    const handlePrevClick = () => {
        const newPageNumber = apiParams.pageNumber - 1;
        if (newPageNumber >= 1) {
            apiParamsDispatcher({ ...apiParams, pageNumber: newPageNumber });
        }
    };

    const handleSortOrderChange = (column) => {
        const unchangedColumns = columnDefinitions
            .filter((c) => c.field !== column.field)
            .map((c) => ({ ...c, sortOrder: '' }));

        const sortField = column.field === 'status' ? 'statusEnumOrder' : column.field;

        setColumnDefinitions([...unchangedColumns, column]);
        apiParamsDispatcher({
            sortField: sortField,
            sortOrder: column.sortOrder
        });
    };

    const handlePrimaryColumnClick = (show) => {
        if (onSelectShow) {
            onSelectShow(show);
        }
    };

    return (
        <div style={{ ...sectionedPanelStyle, padding: '20px 40px 40px' }}>
            <div style={gridContainerStyle}>
                <div style={{ ...gridItemStyle, display: 'flex' }}>
                    <span style={showTitleStyle}>Select a Show</span>
                </div>
                <div style={{ ...gridItemStyle }}>
                    <AsSearchBar
                        id='showSearch'
                        placeholder={window.translate(apiParams.reservations ? 'Search Shows...' : 'Search...')}
                        width={395}
                        height={42}
                        currentSearchTerm={apiParams.searchTerm}
                        onSearchUpdate={handleSearchUpdate}
                    />
                </div>

                <div
                    style={{
                        ...gridItemStyle,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}
                >
                    <LogSelection
                        selectedStations={selectedStations}
                        onApply={setSelectedStations}
                        networks={networks}
                        maxVisibleLength={557}
                        title={window.translate('Networks / Stations')}
                        id='networks'
                        name='networks'
                        containerStyle={{ width: '40%' }}
                    />
                </div>
                <div style={{ ...gridItemStyle, ...tabAreaStyle }}></div>
                <div style={{ ...gridItemStyle, ...tableAreaStyle }}>
                    <AsGrid
                        style={{
                            borderLeft: '1px solid rgb(215, 215, 215)'
                        }}
                        columnDefs={columnDefinitions}
                        pageInfo={pageInfo}
                        data={shows}
                        primaryColumn='name'
                        onPageSizeChange={handlePageSizeChange}
                        onNextClick={handleNextClick}
                        onPrevClick={handlePrevClick}
                        onSortOrderChange={handleSortOrderChange}
                        onPrimaryColumnClick={handlePrimaryColumnClick}
                        showColumnFilter={false}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                        locale={locale}
                        currency={currency}
                    />
                </div>
            </div>
        </div>
    );
};

export default ShowPanel;
